class AddressService {
  constructor() {
    this.autocompleteService = null;
    this.placesService = null;
    this.sessionToken = null;
    this.isGoogleMapsLoaded = false;
  }

  // Initialize Google Maps API
  async initializeGoogleMaps(apiKey) {
    if (this.isGoogleMapsLoaded) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      if (window.google && window.google.maps && window.google.maps.places) {
        this.isGoogleMapsLoaded = true;
        this.initializeServices();
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=sk`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        // Wait longer for the API to fully initialize and avoid timing issues
        const checkApiReady = (attempts = 0) => {
          if (attempts > 50) { // Max 5 seconds
            reject(new Error('Google Maps API initialization timeout'));
            return;
          }
          
          if (window.google && 
              window.google.maps && 
              window.google.maps.places && 
              window.google.maps.places.AutocompleteService &&
              window.google.maps.places.PlacesService) {
            this.isGoogleMapsLoaded = true;
            this.initializeServices();
            resolve();
          } else {
            setTimeout(() => checkApiReady(attempts + 1), 100);
          }
        };
        
        checkApiReady();
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load Google Maps API'));
      };
      
      document.head.appendChild(script);
    });
  }

  // Initialize Google Places services
  initializeServices() {
    if (window.google && window.google.maps && window.google.maps.places) {
      try {
        this.autocompleteService = new window.google.maps.places.AutocompleteService();
        this.placesService = new window.google.maps.places.PlacesService(
          document.createElement('div')
        );
        this.sessionToken = new window.google.maps.places.AutocompleteSessionToken();
      } catch (error) {
        console.error('Error initializing Google Places services:', error);
        this.autocompleteService = null;
        this.placesService = null;
        this.sessionToken = null;
      }
    }
  }

  // Get address predictions based on input
  async getAddressPredictions(input) {
    if (!this.autocompleteService) {
      throw new Error('Google Maps API not initialized');
    }

    if (!input || input.length < 2) {
      return [];
    }

    return new Promise((resolve, reject) => {
      const request = {
        input: input,
        sessionToken: this.sessionToken,
        componentRestrictions: { country: ['sk', 'cz'] }, // Restrict to Slovakia and Czech Republic
        types: ['address']
      };

      this.autocompleteService.getPlacePredictions(request, (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const formattedPredictions = predictions.map(prediction => ({
            placeId: prediction.place_id,
            description: prediction.description,
            mainText: prediction.structured_formatting.main_text,
            secondaryText: prediction.structured_formatting.secondary_text,
            types: prediction.types
          }));

          resolve(formattedPredictions);
        } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          resolve([]);
        } else {
          reject(new Error(`Places service error: ${status}`));
        }
      });
    });
  }

  // Get detailed address information by place ID
  async getPlaceDetails(placeId) {
    if (!this.placesService) {
      throw new Error('Google Maps API not initialized');
    }

    return new Promise((resolve, reject) => {
      const request = {
        placeId: placeId,
        fields: [
          'address_components',
          'formatted_address',
          'geometry',
          'name'
        ],
        sessionToken: this.sessionToken
      };

      this.placesService.getDetails(request, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const addressComponents = this.parseAddressComponents(place.address_components);
          resolve({
            ...addressComponents,
            formattedAddress: place.formatted_address,
            placeId: placeId,
            coordinates: {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            }
          });
        } else {
          reject(new Error(`Place details error: ${status}`));
        }
      });
    });
  }

  // Parse address components into structured format
  parseAddressComponents(components) {
    const addressData = {
      street: '',
      streetNumber: '',
      city: '',
      postalCode: '',
      country: '',
      region: ''
    };

    components.forEach(component => {
      const types = component.types;

      if (types.includes('street_number')) {
        addressData.streetNumber = component.long_name;
      } else if (types.includes('route')) {
        addressData.street = component.long_name;
      } else if (types.includes('postal_town')) {
        addressData.city = component.long_name;
      } else if (types.includes('administrative_area_level_2') && !addressData.city) {
        // Fallback for Slovak cities that might be marked as administrative_area_level_2
        addressData.city = component.long_name;
      } else if (types.includes('sublocality') && !addressData.city) {
        // Another fallback for cities
        addressData.city = component.long_name;
      } else if (types.includes('postal_code')) {
        addressData.postalCode = component.long_name;
      } else if (types.includes('country')) {
        addressData.country = component.long_name;
      } else if (types.includes('administrative_area_level_1')) {
        addressData.region = component.long_name;
      }
    });

    // Combine street number and street name
    if (addressData.streetNumber && addressData.street) {
      addressData.fullStreet = `${addressData.street} ${addressData.streetNumber}`;
    } else {
      addressData.fullStreet = addressData.street || addressData.streetNumber;
    }

    return addressData;
  }

  // Create new session token for new search session
  createNewSessionToken() {
    if (window.google && window.google.maps && window.google.maps.places) {
      try {
        this.sessionToken = new window.google.maps.places.AutocompleteSessionToken();
      } catch (error) {
        console.error('Error creating new session token:', error);
      }
    }
  }
}

// Create singleton instance
const addressService = new AddressService();

export default addressService;
